/** An AI text that could not be written is an error, never template text passed off as the AI's. */
import { toast } from 'sonner';
import { codeOf, detailsOf } from './sendLabels';

export const AI_COMPOSE_FAILED = 'AI_COMPOSE_FAILED';
export const AI_NOT_CONFIGURED = 'AI_NOT_CONFIGURED';

export interface AiFailure {
  message: string;
  /** Go's raw reason (agent_timeout, lint:…): the admin sees it as it is. */
  reason: string;
  retryable: boolean;
  /** Only a nudge / follow-up whose sequence has this step: what the Template button reads. */
  templateStep?: number;
}

export const NOT_WORKING_COPY = "The assistant isn't working for now.";
const NOT_SET_UP_COPY = "The assistant isn't set up in this environment.";
const SUBJECT_COPY = "The assistant couldn't write the subject.";

/** Reasons a retry cannot fix, for an outcome that carries no retryable flag. */
const NOT_RETRYABLE = new Set(['agent_unconfigured', 'agent_auth', 'agent_rejected']);
const SUBJECT_REASONS = new Set(['subject_failed', 'subject_rejected', 'subject_busy']);

/** The AI failure an API error carries (502/503 AI_COMPOSE_FAILED, 503 AI_NOT_CONFIGURED), or null for any other error. */
export function aiFailureOf(error: unknown): AiFailure | null {
  const code = codeOf(error);
  if (code !== AI_COMPOSE_FAILED && code !== AI_NOT_CONFIGURED) return null;
  const details = detailsOf(error);
  const notConfigured = code === AI_NOT_CONFIGURED;
  const reason = typeof details?.reason === 'string' ? details.reason : notConfigured ? 'agent_unconfigured' : '';
  const step = details?.template_step;
  return {
    message: notConfigured ? NOT_SET_UP_COPY : NOT_WORKING_COPY,
    reason,
    retryable: !notConfigured && details?.retryable !== false,
    ...(typeof step === 'number' && step > 0 ? { templateStep: step } : {}),
  };
}

export function isAiOutcomeReason(reason: string): boolean {
  return /^(agent_|subject_|lint:)/.test(reason) || reason === 'invalid_draft' || reason === 'context_unavailable';
}

export const isSubjectReason = (reason: string): boolean => SUBJECT_REASONS.has(reason);

/** The AI failure behind a failed draft_initial item: Go's retryable flag decides, and without one the reason does. */
export function aiFailureOfReason(reason: string, retryable?: boolean): AiFailure | null {
  if (!isAiOutcomeReason(reason)) return null;
  return {
    message: reason === 'agent_unconfigured' ? NOT_SET_UP_COPY : isSubjectReason(reason) ? SUBJECT_COPY : NOT_WORKING_COPY,
    reason,
    retryable: retryable ?? !NOT_RETRYABLE.has(reason),
  };
}

export function toastAiFailure(failure: AiFailure, extra?: string): void {
  const description = [failure.reason, extra].filter(Boolean).join(' · ');
  toast.error(failure.message, description ? { description } : undefined);
}
